import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import Helmet from 'react-helmet'
import classnames from 'classnames'
import throttle from 'lodash.throttle'
import assign from 'lodash.assign'

import {
  generatePageDataFromPathname,
  getWpPageData
} from '../../utils/wordpress'
import { reActivateOptimizely } from '../../utils/optimizely'
import { trackEvent, trackPageView } from '../../utils/tracking'

import { requestWpPageDataSuccess } from '../../reducers/wpData'

import TopBanner from '../components/TopBanner'
import Header from '../components/Header'
import HeaderSeeds from '../components/seeds/HeaderSeeds'
import Footer from '../components/Footer'
import Modal from '../components/Modal'
import CookiesAndAnalyticsInfo from '../components/CookiesAndAnalyticsInfo'

import { IS_NODE, IS_PROD, IS_WEB } from '../../config/'

const DEFAULT_SHARE_IMG = 'https://snips.ai/static/images/snips_share_9.png'
const DEFAULT_PAGE_TITLE = 'Snips - Using Voice to Make Technology Disappear'

const SCROLL_BUCKETS = {
  0: false,
  25: false,
  50: false,
  75: false,
  99: false
}

class App extends Component {
  constructor () {
    super()
    this.state = {
      isAtScrollZero: true
    }
    this._hasScrolledTo = assign({}, SCROLL_BUCKETS)
    this._requestPageDataFromWpMaybe = this._requestPageDataFromWpMaybe.bind(
      this
    )
    this._handleScroll = this._handleScroll.bind(this)
  }
  componentDidMount () {
    this._throttledScroll = throttle(this._handleScroll, 200)
    window.addEventListener('scroll', this._throttledScroll)
    trackPageView({ trackOnGa: false })
  }
  componentWillUnmount () {
    window.removeEventListener('scroll', this._throttledScroll)
  }
  componentDidUpdate (prevProps) {
    reActivateOptimizely()
    if (prevProps.location.pathname !== this.props.location.pathname) {
      trackPageView()
      this._requestPageDataFromWpMaybe().then(() => {
        // reset scroll tracking.
        // need to wait until we have page data, since page will not have full height until then
        this._hasScrolledTo = assign({}, SCROLL_BUCKETS)
      })
    }
  }
  componentWillMount () {
    this._requestPageDataFromWpMaybe()
  }
  _handleScroll () {
    const yOffset = window.pageYOffset || document.documentElement.scrollTop
    const scrolledTo = Math.round(
      (yOffset + window.innerHeight) / document.body.clientHeight * 100
    )

    if (this.state.isAtScrollZero && yOffset > 0) {
      this.setState({ isAtScrollZero: false })
    } else if (!this.state.isAtScrollZero && yOffset === 0) {
      this.setState({ isAtScrollZero: true })
    }

    Object.keys(this._hasScrolledTo).forEach(scrollBucketKey => {
      if (
        scrolledTo >= scrollBucketKey &&
        !this._hasScrolledTo[scrollBucketKey]
      ) {
        trackEvent('ScrollToPercentage', scrollBucketKey, {
          trackOnMixpanel: false
        })
        this._hasScrolledTo[scrollBucketKey] = true
      }
    })
  }
  _requestPageDataFromWpMaybe () {
    // UPDATE: Jan 2017: We used to not return early here on staging,
    // so we could ensure fresh WP data (from wp api) was always requested,
    // to make it easier for non devs who updated the WP CMS to get a preview...
    // Now because the WP api has gotten too slow, we never request fresh data,
    // and previewing might as well be done on live site (build takes ~2min;
    // again primarily because of slow WP api). if important, we need to fix so
    // wp server is faster, to bring this back again.
    if (IS_NODE || (IS_PROD && this.props.pageDataFromWp)) {
      // For Node (SSR), we pre-fetch the data and put it in the store.

      // Otherwise here we already have the data, return. Theoretically we might want to
      // cleare a stale cache after a period of time, but not important for now.
      return Promise.resolve()
    }

    const wpPageId = this.props.routes[1].wpPageId
    if (!wpPageId) {
      // some pages are not on WP at all,
      // or request their custom data themselves (like BlogPostPage)
      return Promise.resolve()
    }
    return getWpPageData(wpPageId).then(this.props.requestWpPageDataSuccess)
  }
  render () {
    const { children, pageData } = this.props
    const { isAtScrollZero } = this.state
    let { instagramData } = this.props

    if (!instagramData && IS_WEB && window.instagramData) {
      try {
        instagramData = window.instagramData
      } catch (e) {
        // we just dont have instagram data to display, that's OK...
      }
    }

    // ideally we would not need to use cloneElement here.
    const pageContent = React.cloneElement(children, {
      data: pageData,
      instagramData
    })

    let headerContent
    let footerContent = <Footer />
    if (pageData.isSeedsPage) {
      headerContent = <HeaderSeeds />
      footerContent = null
    } else {
      headerContent = <Header isAtScrollZero={isAtScrollZero} />
    }

    const topBannerContent = false // pageData && pageData.acf && pageData.acf.topBannerContent
    const withTopBanner = !!topBannerContent // pageData && pageData.acf && pageData.acf.topBannerContent

    const classes = classnames({
      [/page/.test(pageData.slug)
        ? pageData.slug
        : `page--${pageData.slug}`]: true,
      'page--with-top-banner': withTopBanner
    })

    let pageTitle = DEFAULT_PAGE_TITLE
    // database issue seems to have reset all page titles to `admin_snips`,
    // which we don't want to render in the html :)
    if (pageData.title.rendered && pageData.title.rendered !== 'admin_snips') {
      pageTitle = pageData.title.rendered
    }

    return (
      <div className={classes}>
        {/* NOTE: Helmet lib content goes into <head> tags */}
        <Helmet
          title={pageTitle}
          meta={[
            { property: 'og:site_name', content: 'Snips' },
            { property: 'twitter:site', content: '@snips' },
            { property: 'twitter:creator', content: '@snips' },
            { property: 'og:title', content: pageData.title.rendered },
            {
              property: 'og:description',
              content:
                'Snips is the first Voice Platform where you can build an Voice Assistant that is Private by Design.'
            },
            {
              property: 'description',
              content:
                'Snips is the first Voice Platform where you can build an Voice Assistant that is Private by Design.'
            },
            { property: 'twitter:card', content: 'summary_large_image' },
            { property: 'og:image', content: DEFAULT_SHARE_IMG },
            { property: 'og:image:width', content: '2400' },
            { property: 'og:image:height', content: '1260' }
          ]}
        />
        {withTopBanner && <TopBanner content={topBannerContent} />}
        {headerContent}

        {pageContent}

        {footerContent}

        <Modal />

        <CookiesAndAnalyticsInfo />
      </div>
    )
  }
}

App.propTypes = {
  children: PropTypes.element.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired
  }).isRequired,
  routes: PropTypes.array.isRequired,
  requestWpPageDataSuccess: PropTypes.func.isRequired
}

function mapStateToProps ({ wpData: { pages } }, ownProps) {
  // hacky way of retrieving the wpPageId, declared on the routes
  // be careful when/if upgrading react-router
  const wpPageId = IS_NODE ? ownProps.wpPageId : ownProps.routes[1].wpPageId
  // if we don't have cached wp data for this page (some pages aren't on wp at all),
  // still provide some generic page data, so pages can rely on it (title etc)
  const pageData =
    pages[wpPageId] || generatePageDataFromPathname(ownProps.location.pathname)
  return {
    pageDataFromWp: !!pages[wpPageId],
    pageData
  }
}

export default connect(mapStateToProps, {
  requestWpPageDataSuccess
})(App)



// WEBPACK FOOTER //
// ./frontend/scripts/react/containers/App.js